import { formatAmount } from "@loot-os/api-contract";
import { Badge } from "@loot-os/ui/components/badge";
import { BoxImage } from "@loot-os/ui/components/box-image";
import { CommandGroup, CommandItem } from "@loot-os/ui/components/command";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";

import { matchesSearch } from "../lib/boxes";
import { useCurrency } from "../lib/currency";
import { useTRPC } from "../lib/trpc";
import { useDebounced } from "../lib/useDebounced";
import { PickerCombobox } from "./PickerCombobox";

/** A box as the picker hands it back: enough to show it in a list before a save. */
export interface PickedBox {
  id: string;
  name: string;
  imageUrl: string;
  priceCents: number;
}

interface PickableBox extends PickedBox {
  slug: string;
  status: "draft" | "active" | "pre_release" | "archived";
}

/**
 * Picks one of the environment's boxes, its own or an enabled box template, to add to a playlist
 * or a live break's boxes; archived boxes and the ones already chosen (`excludeIds`) are left out.
 * The server judges eligibility (categories, the minimum box price) when the list is saved.
 */
export function LiveBreakBoxPicker({
  excludeIds,
  disabled,
  triggerLabel = "Add a box…",
  onPick,
}: {
  excludeIds: ReadonlySet<string>;
  disabled?: boolean;
  triggerLabel?: string;
  onPick: (box: PickedBox) => void;
}) {
  const trpc = useTRPC();
  const currency = useCurrency();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const search = useDebounced(query.trim());

  const own = useQuery({
    ...trpc.boxes.list.queryOptions({ search: search || undefined }),
    enabled: open,
    placeholderData: (previous) => previous,
  });
  // Small and unpaginated, so it is filtered in memory, as the Boxes page does.
  const templates = useQuery({ ...trpc.boxTemplates.list.queryOptions(), enabled: open });

  const pickable = (box: PickableBox) => box.status !== "archived" && !excludeIds.has(box.id);
  const ownBoxes: PickableBox[] = (own.data ?? []).filter(pickable);
  const templateBoxes: PickableBox[] = (templates.data ?? []).filter(
    (box) => box.enabled && pickable(box) && matchesSearch(box, search),
  );
  const loading = own.isFetching || templates.isFetching;

  const row = (box: PickableBox) => (
    <CommandItem
      key={box.id}
      value={box.id}
      onSelect={() => {
        onPick({ id: box.id, name: box.name, imageUrl: box.imageUrl, priceCents: box.priceCents });
        setOpen(false);
        setQuery("");
      }}
    >
      <BoxImage src={box.imageUrl} className="size-8 shrink-0 rounded border object-contain" />
      <span className="min-w-0 flex-1 truncate">{box.name}</span>
      {(box.status === "draft" || box.status === "pre_release") && (
        <Badge variant="outline">{box.status === "pre_release" ? "pre-release" : "draft"}</Badge>
      )}
      <span className="shrink-0 font-mono text-xs text-muted-foreground">
        {formatAmount(box.priceCents, currency)}
      </span>
    </CommandItem>
  );

  return (
    <PickerCombobox
      open={open}
      onOpenChange={setOpen}
      query={query}
      onQueryChange={setQuery}
      triggerLabel={triggerLabel}
      searchPlaceholder="Search name, slug or id"
      emptyMessage={loading ? "Searching…" : "No matching boxes."}
      disabled={disabled}
    >
      {ownBoxes.length > 0 && <CommandGroup heading="Your boxes">{ownBoxes.map(row)}</CommandGroup>}
      {templateBoxes.length > 0 && (
        <CommandGroup heading="Box templates">{templateBoxes.map(row)}</CommandGroup>
      )}
    </PickerCombobox>
  );
}
